import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithEmailLink, isSignInWithEmailLink, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';
import { isWebAuthnSupported, getCredential } from '../utils/webauthn';
import { getPasskeySignInErrorMessage } from '../utils/passkeyMessages';

const Landing = () => {
  const [showSignUp, setShowSignUp] = useState(false);
  const [authMode, setAuthMode] = useState('passwordless'); // 'password' or 'passwordless'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordlessEmailSent, setPasswordlessEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();
  const { currentUser } = useAuth();

  // If user is already logged in, redirect to studio
  React.useEffect(() => {
    if (currentUser) {
      navigate('/studio');
    }
  }, [currentUser, navigate]);

  // Handle email link sign-in when user returns from clicking the link
  React.useEffect(() => {
    const handleEmailLinkSignIn = async () => {
      // Check if the URL is a sign-in link
      if (isSignInWithEmailLink(auth, window.location.href)) {
        setLoading(true);
        try {
          // Get the email from localStorage or URL parameter
          let emailForSignIn = window.localStorage.getItem('emailForSignIn');

          // If email is not in localStorage, try to get it from URL params
          if (!emailForSignIn) {
            const urlParams = new URLSearchParams(window.location.search);
            emailForSignIn = urlParams.get('email');
          }

          if (!emailForSignIn) {
            // If still no email, prompt the user
            setError('Please enter your email to complete sign-in');
            setAuthMode('passwordless');
            setLoading(false);
            return;
          }

          // Complete the sign-in with Firebase Auth
          const result = await signInWithEmailLink(auth, emailForSignIn, window.location.href);

          // SECURITY CHECK: Verify the user exists in our Firestore users collection
          const userDocRef = doc(db, 'users', result.user.uid);
          const userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            // User doesn't exist in our system - this is a new account created by accident
            // Delete the Firebase Auth user and reject the sign-in
            await result.user.delete();
            setError('No account found with this email. Please sign up first or use the correct email address.');
            window.localStorage.removeItem('emailForSignIn');
            window.history.replaceState({}, document.title, window.location.pathname);
            setLoading(false);
            return;
          }

          // Clear the email from localStorage
          window.localStorage.removeItem('emailForSignIn');

          // Clear the URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);

          // Navigate to studio
          navigate('/studio');
        } catch (error) {
          console.error('Error signing in with email link:', error);
          setError('Failed to sign in with email link. Please try again.');
          setLoading(false);
        }
      }
    };

    handleEmailLinkSignIn();
  }, [auth, navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/studio');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordlessSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const requestMagicLinkSignIn = httpsCallable(functions, 'requestMagicLinkSignIn');
      await requestMagicLinkSignIn({
        email,
        continueUrl: `${window.location.origin}/?email=${encodeURIComponent(email)}`,
      });
      window.localStorage.setItem('emailForSignIn', email);
      setPasswordlessEmailSent(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        fullName: fullName,
        role: 'user_verified_static_1',
        createdAt: new Date(),
        consent: {
          termsOfService: 'v1.0',
          privacyPolicy: 'v1.0',
          timestamp: new Date(),
        },
      });

      navigate('/studio');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user document exists, if not create it
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          fullName: user.displayName || '',
          role: 'user_verified_static_1',
          createdAt: new Date(),
          consent: {
            termsOfService: 'v1.0',
            privacyPolicy: 'v1.0',
            timestamp: new Date(),
          },
        });
      }

      navigate('/studio');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user document exists, if not create it
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          fullName: user.displayName || 'Apple User',
          role: 'user_verified_static_1',
          createdAt: new Date(),
          consent: {
            termsOfService: 'v1.0',
            privacyPolicy: 'v1.0',
            timestamp: new Date(),
          },
        });
      }

      navigate('/studio');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeySignIn = async () => {
    if (!isWebAuthnSupported()) {
      setError('Passkeys are not supported on this device');
      return;
    }
    if (!email) {
      setError('Please enter your email');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const generateOptions = httpsCallable(functions, 'generatePasskeyAuthenticationOptions');
      const optionsResult = await generateOptions({
        email,
        origin: window.location.origin,
      });
      const credential = await getCredential(optionsResult.data.options);

      const authenticatePasskey = httpsCallable(functions, 'authenticatePasskey');
      const result = await authenticatePasskey({
        email,
        origin: window.location.origin,
        challengeId: optionsResult.data.challengeId,
        assertion: credential
      });

      if (result.data.success && result.data.customToken) {
        // Sign in with custom token
        await signInWithCustomToken(auth, result.data.customToken);
        navigate('/studio');
      } else {
        setError('Failed to authenticate with passkey');
      }
    } catch (error) {
      console.error('Error with passkey sign-in:', error);
      setError(getPasskeySignInErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .auth-container {
          font-family: 'Roboto', sans-serif;
          background-color: #f8f9fa;
          color: #202124;
        }
        
        .auth-card {
          background: #ffffff;
          border: 1px solid #dadce0;
          border-radius: 16px;
          padding: 32px 40px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          width: 100%;
          max-width: 400px;
        }
        
        .auth-card-header {
          text-align: center;
          margin-bottom: 24px;
        }
        
        .auth-card-header h1 {
          font-family: 'Google Sans', sans-serif;
          font-size: 24px;
          font-weight: 500;
          margin-bottom: 0;
        }
        
        .auth-card-header p {
          color: #5f6368;
          font-size: 16px;
          margin: 0;
        }
        
        .auth-form-group { 
          position: relative;
          margin-bottom: 16px; 
          margin-top: 16px;
        }
        
        .auth-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          border: 1px solid #dadce0;
          border-radius: 8px;
          overflow: hidden;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        
        .auth-input-wrapper:focus-within {
          border-color: #1a73e8;
          box-shadow: 0 0 0 2px #e8f0fe;
        }
        
        .auth-form-input {
          width: 100%;
          padding: 13px 16px 11px 16px;
          border: none;
          font-size: 16px;
          font-family: 'Roboto', sans-serif;
          background: white;
          transition: none;
          line-height: 1.5;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        .auth-form-input:focus {
          outline: none;
        }
        
        .auth-form-input::placeholder {
          color: transparent;
        }
        
        .auth-icon-segment {
          display: flex;
          align-items: stretch;
          justify-content: center;
          width: 56px;
          min-height: 48px;
          background-color: #f8f9fa;
          border-left: 1px solid #dadce0;
          cursor: pointer;
          transition: background-color 0.2s ease;
          position: relative;
        }
        
        .auth-icon-segment::before {
          content: '';
          position: absolute;
          left: -1px;
          top: 0;
          bottom: 0;
          width: 1px;
          background-color: #dadce0;
        }
        
        .auth-icon-segment:hover {
          background-color: #e8eaed;
        }
        
        .auth-icon-segment svg,
        .auth-icon-segment i {
          width: 18px;
          height: 18px;
          color: #5f6368;
          align-self: center;
          font-size: 18px;
        }
        
        .auth-floating-label {
          position: absolute;
          left: 16px;
          top: 15px;
          font-size: 16px;
          font-family: 'Roboto', sans-serif;
          color: #5f6368;
          background: transparent;
          padding: 0;
          pointer-events: none;
          transition: all 0.2s ease;
          transform-origin: left top;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        .auth-form-group:focus-within .auth-floating-label {
          top: -9px;
          left: 12px;
          font-size: 12px;
          color: #1a73e8;
          background: white;
          padding: 0 4px;
        }
        
        .auth-form-group:has(.auth-form-input:not(:placeholder-shown)) .auth-floating-label {
          top: -9px;
          left: 12px;
          font-size: 12px;
          color: #5f6368;
          background: white;
          padding: 0 4px;
        }
        
        .auth-form-group:focus-within:has(.auth-form-input:not(:placeholder-shown)) .auth-floating-label {
          color: #1a73e8;
        }

        .auth-btn { 
          padding: 12px 24px; 
          border: 1px solid transparent; 
          border-radius: 8px; 
          cursor: pointer; 
          font-weight: 500; 
          font-size: 15px; 
          transition: all 0.2s ease; 
          text-decoration: none; 
          display: inline-flex; 
          align-items: center; 
          justify-content: center;
          gap: 12px; 
          width: 100%;
        }
        
        .auth-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .auth-btn-primary { 
          background: #1a73e8; 
          color: white; 
        }
        .auth-btn-primary:hover:not(:disabled) { 
          box-shadow: 0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15); 
        }
        .auth-btn-secondary {
          background: #f8f9fa;
          color: #5f6368;
          border: 1px solid #e0e0e0;
        }
        .auth-btn-secondary:hover:not(:disabled) {
          background-color: #f1f3f4;
          border-color: #d2d2d2;
        }
        
        .auth-divider {
          display: flex;
          align-items: center;
          text-align: center;
          color: #5f6368;
          font-size: 14px;
          margin: 24px 0;
        }
        
        .auth-divider::before, .auth-divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid #dadce0;
        }
        
        .auth-divider:not(:empty)::before {
          margin-right: .75em;
        }
        
        .auth-divider:not(:empty)::after {
          margin-left: .75em;
        }
        
        .auth-social-icons {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-bottom: 20px;
        }
        
        .auth-social-icon-btn {
          width: 48px;
          height: 48px;
          border: 1px solid #dadce0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
        }
        
        .auth-social-icon-btn:hover {
          background-color: #f8f9fa;
          border-color: #c0c0c0;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .auth-social-icon-btn svg,
        .auth-social-icon-btn img {
          width: 24px;
          height: 24px;
        }
        
        .auth-error {
          background-color: #fce8e6;
          color: #d93025;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 16px;
        }
        .auth-footer-links {
          text-align: center;
          margin-top: 24px;
          font-size: 14px;
          color: #5f6368;
        }
        .auth-footer-links a {
          color: #1a73e8;
          text-decoration: none;
          cursor: pointer;
        }
        .auth-footer-links a:hover {
          text-decoration: underline;
        }
        .auth-buttons-stack {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 28px;
        }
        
        .auth-info-tooltip {
          display: inline-block;
          margin-left: 4px;
          width: 16px;
          height: 16px;
          background-color: #e8f0fe;
          color: #1a73e8;
          border-radius: 50%;
          text-align: center;
          line-height: 16px;
          font-size: 12px;
          cursor: help;
          position: relative;
        }
        
        .auth-info-tooltip:hover::after {
          content: 'You can use your email address or phone number to sign in';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #333;
          color: white;
          padding: 8px 12px;
          border-radius: 4px;
          white-space: nowrap;
          font-size: 12px;
          margin-bottom: 4px;
        }
        
        .auth-info-tooltip:hover::before {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 4px solid transparent;
          border-top-color: #333;
        }
        
        .auth-success-message {
          background-color: #e6f4ea;
          color: #137333;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 16px;
          text-align: center;
        }
        
      `}</style>

      <div className="h-screen flex bg-gray-50 overflow-hidden">
        {/* Left Panel - Hidden on mobile */}
        <div className="hidden lg:flex lg:w-1/2 bg-cover bg-center text-white p-12 flex-col justify-between relative"
          style={{
            backgroundImage: "url('/images/hero-image.webp')",
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60"></div>

          {/* ADU LAB logo */}
          <div className="absolute top-[8%] left-0 right-0 text-center z-10">
            <div className="text-sm font-light text-white/75 tracking-[0.3em]" style={{ fontFamily: "'Google Sans', sans-serif" }}>
              ADU <span className="inline-block w-20 h-px bg-white/75 align-middle mx-2"></span> LAB
            </div>
          </div>

          {/* Tagline */}
          <div className="absolute top-[12%] left-0 right-0 text-center z-10">
            <h2 className="text-3xl font-medium text-white/95" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
              Unlock Your Property's Potential
            </h2>
          </div>

          {/* Top button with glassmorphism effect */}
          <div className="absolute top-[20%] left-0 right-0 flex justify-center z-10">
            <Link
              to="/pricing"
              className="px-8 py-4 text-lg font-medium backdrop-blur-md bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all duration-300 shadow-lg text-white/95"
              style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}
            >
              View Plans & Pricing
            </Link>
          </div>

          {/* Bottom tagline */}
          <div className="absolute bottom-20 left-0 right-0 text-center z-10 px-8">
            <Link
              to="/assessment"
              className="inline-block px-6 py-3 text-base font-medium backdrop-blur-md bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all duration-300 shadow-lg text-white/95 mb-4"
              style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}
            >
              Start Free Assessment
            </Link>
            <p className="text-lg text-white/85" style={{ fontFamily: "'Roboto', sans-serif", letterSpacing: '0.01em' }}>
              Get your free feasibility assessment, and see how<br />an ADU could unlock your next chapter.
            </p>
          </div>

          {/* Copyright at bottom */}
          <div className="absolute bottom-4 left-4 right-4 text-center text-xs text-white/85 z-10">
            <p>© 2025 ADU Lab by <a href="https://44laboratories.com" target="_blank" rel="noopener noreferrer" className="hover:text-white">44 Laboratories, LLC</a>. All rights reserved.</p>
          </div>
        </div>

        {/* Right Panel - Full width on mobile */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-8 lg:p-12 auth-container">
          <div className="w-full max-w-md">
            <div className="auth-card">
              {!showSignUp ? (
                // Sign In Card
                <>
                  <div className="auth-logo" style={{ marginBottom: '24px', textAlign: 'center' }}>
                    <div className="text-sm font-light tracking-[0.3em]" style={{ fontFamily: "'Google Sans', sans-serif", color: '#5f6368' }}>
                      ADU <span className="inline-block h-px align-middle mx-1" style={{ width: '60px', backgroundColor: '#5f6368' }}></span> LAB
                    </div>
                  </div>

                  <div className="auth-card-header">
                    <h1>Welcome back</h1>
                  </div>

                  {error && <div className="auth-error">{error}</div>}

                  {passwordlessEmailSent ? (
                    <div className="auth-success-message">
                      Check your email! We've sent you a sign-in link.
                    </div>
                  ) : (
                    <>
                      {authMode === 'passwordless' ? (
                        // Passwordless email form
                        <form onSubmit={handlePasswordlessSignIn}>
                          <div className="auth-form-group">
                            <div className="auth-input-wrapper">
                              <input
                                type="email"
                                className="auth-form-input"
                                placeholder=" "
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                id="passwordless-email"
                              />
                            </div>
                            <label className="auth-floating-label" htmlFor="passwordless-email">
                              Enter your email
                            </label>
                          </div>
                          <div className="auth-buttons-stack">
                            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
                              {loading ? 'Sending link...' : 'Send Sign In Link'}
                            </button>
                            <button
                              type="button"
                              className="auth-btn auth-btn-secondary"
                              onClick={() => setAuthMode('password')}
                            >
                              Sign in with password
                            </button>
                          </div>
                        </form>
                      ) : (
                        // Regular username/password form
                        <form onSubmit={handleSignIn}>
                          <div className="auth-form-group">
                            <div className="auth-input-wrapper">
                              <input
                                type="email"
                                className="auth-form-input"
                                placeholder=" "
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                id="signin-email"
                              />
                              <div
                                className="auth-icon-segment"
                                title="Use email link instead"
                                onClick={() => setAuthMode('passwordless')}
                              >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                              </div>
                            </div>
                            <label className="auth-floating-label" htmlFor="signin-email">
                              Enter your email
                            </label>
                          </div>
                          <div className="auth-form-group">
                            <div className="auth-input-wrapper">
                              <input
                                type={showPassword ? "text" : "password"}
                                className="auth-form-input"
                                placeholder=" "
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength="6"
                                title="Password must be at least 6 characters"
                                id="signin-password"
                              />
                              <div
                                className="auth-icon-segment"
                                title="Show password"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                <i className="material-icons">
                                  {showPassword ? 'visibility_off' : 'visibility'}
                                </i>
                              </div>
                            </div>
                            <label className="auth-floating-label" htmlFor="signin-password">
                              Enter your password
                            </label>
                          </div>
                          <div className="auth-buttons-stack">
                            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
                              {loading ? 'Signing in...' : 'Sign In'}
                            </button>
                          </div>
                        </form>
                      )}
                    </>
                  )}

                  {/* Social login buttons moved to bottom */}
                  <div
                    className="auth-divider"
                    style={{
                      marginTop: '20px',
                      cursor: authMode === 'password' ? 'pointer' : 'default',
                      color: authMode === 'password' ? '#1a73e8' : '#5f6368'
                    }}
                    onClick={() => authMode === 'password' && setAuthMode('passwordless')}
                  >
                    {authMode === 'password' ? 'Or Email me a login link' : 'Or Sign in with'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', marginTop: '16px' }}>
                    <button
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                      title="Sign in with Google"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#fff',
                        border: '1px solid #dadce0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        opacity: loading ? 0.7 : 1,
                        height: '42px'
                      }}
                      onMouseOver={(e) => {
                        if (!loading) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#d2d3d6';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.borderColor = '#dadce0';
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                    </button>
                    <button
                      onClick={handleAppleSignIn}
                      disabled={loading}
                      title="Sign in with Apple"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#fff',
                        border: '1px solid #dadce0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        opacity: loading ? 0.7 : 1,
                        height: '42px'
                      }}
                      onMouseOver={(e) => {
                        if (!loading) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#d2d3d6';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.borderColor = '#dadce0';
                      }}
                    >
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="#000">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                    </button>
                    <button
                      onClick={handlePasskeySignIn}
                      disabled={loading}
                      title="Sign in with Passkey"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#fff',
                        border: '1px solid #dadce0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        opacity: loading ? 0.7 : 1,
                        height: '42px',
                        minWidth: '42px'
                      }}
                      onMouseOver={(e) => {
                        if (!loading) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#d2d3d6';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.borderColor = '#dadce0';
                      }}
                    >
                      <img src="/auth-icons/passkey-icon.jpg" alt="Passkey" width="28" height="28" style={{ borderRadius: '2px' }} />
                    </button>
                  </div>

                  <div className="auth-footer-links" style={{ marginTop: '16px' }}>
                    <p style={{
                      textAlign: 'center',
                      fontSize: '14px',
                      color: '#5f6368',
                      lineHeight: '1.5',
                      marginBottom: '12px',
                      fontFamily: "'Roboto', sans-serif"
                    }}>
                      By signing in, you agree to<br />
                      our <Link to="/terms" style={{ color: '#1a73e8', textDecoration: 'none' }}>Terms of Service</Link>
                      {' and '}
                      <Link to="/privacy" style={{ color: '#1a73e8', textDecoration: 'none' }}>Privacy Policy</Link>
                    </p>
                    <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '16px', alignItems: 'center' }}>
                      <Link to="/forgot-password" style={{ fontSize: '14px', color: '#1a73e8', fontFamily: "'Roboto', sans-serif" }}>Forgot password?</Link>
                      <span style={{ color: '#5f6368' }}>•</span>
                      <Link to="/assessment" style={{ fontSize: '14px', color: '#202124', fontFamily: "'Roboto', sans-serif", textDecoration: 'none' }}>Start Assessment</Link>
                    </div>
                  </div>
                </>
              ) : (
                // Create Account Card
                <>
                  <div className="auth-card-header">
                    <h1>Create your account</h1>
                    <p>Start your ADU journey today.</p>
                  </div>

                  {error && <div className="auth-error">{error}</div>}

                  <form onSubmit={handleSignUp}>
                    <div className="auth-form-group">
                      <label className="auth-form-label">Full Name</label>
                      <input
                        type="text"
                        className="auth-form-input"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="auth-form-group">
                      <label className="auth-form-label">Email Address</label>
                      <input
                        type="email"
                        className="auth-form-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="auth-form-group">
                      <label className="auth-form-label">Password</label>
                      <input
                        type="password"
                        className="auth-form-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength="6"
                        title="Password must be at least 6 characters"
                      />
                    </div>
                    <button type="submit" className="auth-btn auth-btn-primary" style={{ marginTop: '8px' }} disabled={loading}>
                      {loading ? 'Creating account...' : 'Create Account'}
                    </button>
                  </form>

                  <div className="auth-footer-links">
                    <a onClick={() => setShowSignUp(false)}>Already have an account? Sign in</a>
                    <div className="mt-4 text-xs text-gray-500">
                      By creating an account, you agree to our{' '}
                      <Link to="/terms" className="hover:underline">Terms of Service</Link>
                      {' and '}
                      <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Landing;
